import React, { useEffect, useState } from 'react'
import {
  Paper,
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Snackbar,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material'
import MuiAlert from '@mui/material/Alert'
import API from '@aws-amplify/api'
import {
  IIngredientRequest,
  IngredientRequest,
} from 'interfaces/IIngredientRequest'
import getSourceOrDestinationLabel from 'shared/getSourceOrDestinationLabel'
import IconButton from '@mui/material/IconButton'
import Collapse from '@mui/material/Collapse'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp'
import { useDispatch } from 'react-redux'
import SmoothLoadingIndicator from 'components/shared/SmoothLoadingIndicator'
import { formatTimestamp } from 'shared/dateFormatter'

export default function IngredientRequestList() {
  const dispatch = useDispatch()
  const [ingredientRequests, setIngredientRequests] = useState<
    IIngredientRequest[]
  >([])
  const [isLoading, setIsLoading] = useState(false)
  const [alertMessage, setAlertMessage] = useState('')
  const [isFailureSnackbarOpen, setIsFailureSnackbarOpen] = useState(false)
  const [isSuccessSnackbarOpen, setIsSuccessSnackbarOpen] = useState(false)
  const [expandedRows, setExpandedRows] = useState<{ [key: string]: boolean }>(
    {}
  )
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false)
  const [selectedRequest, setSelectedRequest] =
    useState<IIngredientRequest | null>(null)

  React.useEffect(() => {
    dispatch({ type: 'SET_PAGE_TITLE', payload: 'Daftar Request Bahan Baku' })
  }, [dispatch])

  const handleSnackbarClose = (): void => {
    setIsFailureSnackbarOpen(false)
    setIsSuccessSnackbarOpen(false)
  }

  const getIngredientRequests = async () => {
    setIsLoading(true)
    try {
      const response = await API.get(
        'products',
        '/ingredients/getIngredientRequests',
        {
          queryStringParameters: { includeDetails: 'true' },
        }
      )
      setIngredientRequests(response.map((r: any) => new IngredientRequest(r)))
    } catch (error) {
      setAlertMessage('Gagal mengambil data request bahan baku.')
      setIsFailureSnackbarOpen(true)
    }
    setIsLoading(false)
  }

  useEffect(() => {
    getIngredientRequests()
  }, [])

  const handleOpenCancelDialog = (req: IIngredientRequest) => {
    setSelectedRequest(req)
    setCancelDialogOpen(true)
  }

  const handleCloseCancelDialog = () => {
    setCancelDialogOpen(false)
    setSelectedRequest(null)
  }

  const handleConfirmCancel = async () => {
    if (!selectedRequest) return
    try {
      setIsLoading(true)
      await API.post('products', '/ingredients/cancelIngredientRequest', {
        body: {
          ingredientRequestId: selectedRequest.ingredientRequestId,
        },
      })
      setAlertMessage('Berhasil membatalkan request.')
      setIsSuccessSnackbarOpen(true)
      await getIngredientRequests()
    } catch (error) {
      console.error(error)
      setAlertMessage(`Gagal membatalkan ${selectedRequest.ingredientRequestDisplayId}`)
      setIsFailureSnackbarOpen(true)
    } finally {
      setIsLoading(false)
      handleCloseCancelDialog()
    }
  }

  const canCancel = (req: IIngredientRequest) => {
    return req.stockCard && !req.stockCard.isConfirmed
  }

  const getCancelReason = (req: IIngredientRequest) => {
    if (!req.stockCard) {
      return 'Tidak ada Kartu Stok terkait.'
    }
    if (req.stockCard.isConfirmed) {
      return 'Kartu Stok sudah dikonfirmasi.'
    }
    return null
  }

  return (
    <Paper>
      <Box p={2}>
        <Typography variant="h6">Daftar Request Bahan Baku</Typography>
        {isLoading && (
          <Box display="flex" justifyContent="center" alignItems="center" p={2}>
            <SmoothLoadingIndicator size={40} />
          </Box>
        )}
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell padding="none"></TableCell>
                <TableCell>Nomor Request Bahan Baku</TableCell>
                <TableCell>Tanggal Request</TableCell>
                <TableCell>Nomor Dokumen Kartu Stok Masuk/Keluar</TableCell>
                <TableCell>No. Bets</TableCell>
                <TableCell>Masuk/Keluar</TableCell>
                <TableCell>Diminta/Diberikan Oleh</TableCell>
                <TableCell>Dari/Tujuan</TableCell>
                <TableCell>Notes Peminta</TableCell>
                <TableCell>Action</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {ingredientRequests.length === 0 && !isLoading && (
                <TableRow>
                  <TableCell colSpan={10} align="center">
                    Tidak ada request bahan baku.
                  </TableCell>
                </TableRow>
              )}
              {ingredientRequests.map((req) => (
                <React.Fragment key={req.ingredientRequestId}>
                  <TableRow
                    hover
                    onClick={() =>
                      setExpandedRows((prev) => ({
                        ...prev,
                        [req.ingredientRequestId]:
                          !prev[req.ingredientRequestId],
                      }))
                    }
                  >
                    <TableCell padding="none">
                      <IconButton aria-label="expand row" size="small">
                        {expandedRows[req.ingredientRequestId] ? (
                          <KeyboardArrowUpIcon />
                        ) : (
                          <KeyboardArrowDownIcon />
                        )}
                      </IconButton>
                    </TableCell>
                    <TableCell>{req.ingredientRequestDisplayId}</TableCell>
                    <TableCell>
                      {formatTimestamp('id-ID', req.createdAt)}
                    </TableCell>
                    <TableCell>
                      {req.stockCard?.stockCardDisplayId ?? '-'}
                    </TableCell>
                    <TableCell>{req.batchNumber || '-'}</TableCell>
                    <TableCell>{req.inOutDirection}</TableCell>
                    <TableCell>{req.createdBy}</TableCell>
                    <TableCell>
                      {getSourceOrDestinationLabel(req.sourceOrDestination)}
                    </TableCell>
                    <TableCell>{req.notes}</TableCell>
                    <TableCell>
                      {canCancel(req) ? (
                        <Button
                          variant="contained"
                          color="secondary"
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation()
                            handleOpenCancelDialog(req)
                          }}
                        >
                          Batalkan
                        </Button>
                      ) : (
                        <Typography variant="caption" color="textSecondary">
                          {getCancelReason(req)}
                        </Typography>
                      )}
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell
                      colSpan={10}
                      style={{ paddingTop: 0, paddingBottom: 0 }}
                    >
                      <Collapse
                        in={expandedRows[req.ingredientRequestId]}
                        timeout="auto"
                        unmountOnExit
                      >
                        <Box margin={1}>
                          <Typography variant="subtitle1" gutterBottom>
                            Detail Bahan Baku
                          </Typography>
                          <Table size="small">
                            <TableHead>
                              <TableRow>
                                <TableCell>Kode Bahan Baku</TableCell>
                                <TableCell>
                                  Qty Diminta/Diberikan (kg)
                                </TableCell>
                                <TableCell>
                                  Qty Dikeluarkan/Dimasukan (kg)
                                </TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {req.stockCard?.stockCardIngredients.map(
                                (ingredient, idx) => (
                                  <TableRow
                                    key={ingredient.ingredientId || idx}
                                  >
                                    <TableCell>
                                      {ingredient.ingredientId}
                                      {' - '}
                                      {ingredient.ingredientName}
                                    </TableCell>
                                    <TableCell>{ingredient.quantity}</TableCell>
                                    <TableCell>
                                      {ingredient.confirmedQuantity ??
                                        'Pending'}
                                    </TableCell>
                                  </TableRow>
                                )
                              )}
                            </TableBody>
                          </Table>
                        </Box>
                      </Collapse>
                    </TableCell>
                  </TableRow>
                </React.Fragment>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
      {/* Cancel Confirmation Dialog */}
      <Dialog
        open={cancelDialogOpen}
        onClose={handleCloseCancelDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Konfirmasi Pembatalan Request</DialogTitle>
        <DialogContent>
          {selectedRequest && (
            <Box>
              <Typography variant="body1" gutterBottom>
                Apakah anda yakin ingin membatalkan request berikut?
              </Typography>
              <Box mt={2} p={2} bgcolor="grey.100" borderRadius="4px">
                <Typography variant="body2">
                  <strong>Nomor Request:</strong>{' '}
                  {selectedRequest.ingredientRequestDisplayId}
                </Typography>
                <Typography variant="body2">
                  <strong>Tanggal:</strong>{' '}
                  {formatTimestamp('id-ID', selectedRequest.createdAt)}
                </Typography>
                <Typography variant="body2">
                  <strong>Kartu Stok:</strong>{' '}
                  {selectedRequest.stockCard?.stockCardDisplayId ?? '-'}
                </Typography>
                <Typography variant="body2">
                  <strong>No. Bets:</strong>{' '}
                  {selectedRequest.batchNumber || '-'}
                </Typography>
                <Typography variant="body2">
                  <strong>Arah:</strong> {selectedRequest.inOutDirection}
                </Typography>

                {selectedRequest.stockCard?.stockCardIngredients &&
                  selectedRequest.stockCard.stockCardIngredients.length > 0 && (
                    <Box mt={2}>
                      <Typography variant="subtitle2" gutterBottom>
                        <strong>Detail Bahan Baku:</strong>
                      </Typography>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Kode Bahan Baku</TableCell>
                            <TableCell>Qty Diminta (kg)</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {selectedRequest.stockCard.stockCardIngredients.map(
                            (ingredient, idx) => (
                              <TableRow key={ingredient.ingredientId || idx}>
                                <TableCell>
                                  {ingredient.ingredientId}
                                  {' - '}
                                  {ingredient.ingredientName}
                                </TableCell>
                                <TableCell>{ingredient.quantity}</TableCell>
                              </TableRow>
                            )
                          )}
                        </TableBody>
                      </Table>
                    </Box>
                  )}
              </Box>
              {getCancelReason(selectedRequest) && (
                <Box mt={2}>
                  <MuiAlert severity="warning">
                    Tidak dapat dibatalkan: {getCancelReason(selectedRequest)}
                  </MuiAlert>
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseCancelDialog}>
            Batal
          </Button>
          {selectedRequest && canCancel(selectedRequest) && (
            <Button
              onClick={handleConfirmCancel}
              color="secondary"
              variant="contained"
              disabled={isLoading}
            >
              {isLoading ? 'Memproses...' : 'Konfirmasi Batalkan'}
            </Button>
          )}
        </DialogActions>
      </Dialog>
      <Snackbar
        open={isSuccessSnackbarOpen}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
      >
        <MuiAlert severity="success" elevation={6} variant="filled">
          {alertMessage}
        </MuiAlert>
      </Snackbar>
      <Snackbar
        open={isFailureSnackbarOpen}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
      >
        <MuiAlert severity="error" elevation={6} variant="filled">
          {alertMessage}
        </MuiAlert>
      </Snackbar>
    </Paper>
  );
}

